import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, History, Loader2, ListPlus, Radio, Square, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { wholeStory } from "@/lib/storyRead";
import { ListenBars } from "@/components/news/ListenBars";

const idOf = (row) => row.story_id || row.url.replace(/\W+/g, "").slice(-16);

const ago = (stamp) => {
  const mins = Math.round((Date.now() - new Date(stamp).getTime()) / 60000);
  if (mins < 60) return `${Math.max(1, mins)} min ago`;
  if (mins < 1440) return `${Math.round(mins / 60)} h ago`;
  return `${Math.round(mins / 1440)} d ago`;
};

// Everything MiLu has read out this week, ready to hear again.
export const ListenHistory = ({ speaker, lang = "", refreshKey = 0, onQueue, isQueued }) => {
  const [rows, setRows] = useState([]);
  const [minutes, setMinutes] = useState(0);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [gettingWeek, setGettingWeek] = useState(false);

  const load = useCallback(async () => {
    try {
      const [heard, counted] = await Promise.all([
        api.get("/news/listened", { params: { days: 7 } }),
        api.get("/news/listened/stats", { params: { days: 7 } }),
      ]);
      setRows(heard.data.rows);
      setMinutes(heard.data.minutes);
      setStats(counted.data);
    } catch (e) {
      console.error("could not load the listen history", e);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (rows.length === 0) return null;
  const shown = showAll ? rows : rows.slice(0, 6);
  const onMyWeek = (speaker.playing || speaker.loading || "").startsWith("week-");

  // the week as a run of short pieces, so it starts talking within seconds
  const playMyWeek = async () => {
    if (onMyWeek) return speaker.stop();
    setGettingWeek(true);
    try {
      const { data } = await api.get("/news/listened/replay", { params: { lang } });
      return speaker.playQueue(data.parts.map((text, i) => ({
        key: `week-${i}`,
        label: data.titles[i] || "My week of listening",
        getText: async () => text,
      })));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not put your week together");
      return null;
    } finally {
      setGettingWeek(false);
    }
  };

  const replay = (row) =>
    speaker.play(`${row.story_id || row.url}-again`,
                 wholeStory({ ...row, lang: row.lang }, lang || row.lang), row.title);

  const clear = async () => {
    try {
      await api.delete("/news/listened");
      setRows([]);
      setMinutes(0);
      setStats(null);
      toast.success("Cleared — nothing kept from this week's listening");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not clear that");
    }
  };

  return (
    <div className="mt-4 rounded-3xl border border-white/10 bg-panel p-4" data-testid="listen-history">
      <div className="flex flex-wrap items-center gap-2">
        <button
          data-testid="listen-history-toggle"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-brand"
        >
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <History className="h-4 w-4" /> Recently listened
          <span className="normal-case tracking-normal text-ink-soft">
            · {rows.length} {rows.length === 1 ? "story" : "stories"} · about {minutes} min this week
          </span>
        </button>
        {open && (
          <button
            data-testid="week-replay-play"
            onClick={playMyWeek}
            className="ms-auto inline-flex min-h-[30px] items-center gap-1.5 rounded-full bg-brand px-3.5 text-xs font-semibold text-white transition-colors hover:bg-brand-hover active:scale-95"
          >
            {gettingWeek ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : onMyWeek ? <Square className="h-3.5 w-3.5" />
              : <Radio className="h-3.5 w-3.5" />}
            {gettingWeek ? "Getting your week…" : onMyWeek ? "Stop" : "Play my week"}
          </button>
        )}
        {open && (
          <button
            data-testid="listen-history-clear"
            onClick={clear}
            aria-label="Clear my listening history"
            className="rounded-full border border-white/10 p-1.5 text-ink-soft transition-colors hover:border-destructive/60 hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {open && (stats?.publishers?.length || stats?.topics?.length) ? (
        <div className="mt-3 grid gap-4 rounded-2xl border border-white/5 bg-page/40 p-3 sm:grid-cols-2"
             data-testid="listen-stats">
          <ListenBars title="Who I listen to" rows={stats.publishers} testid="stats-publisher" />
          <ListenBars title="What I listen to" rows={stats.topics} testid="stats-topic" />
        </div>
      ) : null}

      {open && (
        <ul className="mt-3 space-y-2">
          {shown.map((row) => {
            const key = `${row.story_id || row.url}-again`;
            const busy = speaker.loading === key;
            const reading = speaker.playing === key;
            return (
              <li key={row.url} className="flex flex-wrap items-start gap-2 text-xs"
                  data-testid={`listened-row-${idOf(row)}`}>
                <span className="min-w-0 flex-1" dir="auto">
                  <a href={row.url} target="_blank" rel="noreferrer"
                     className="text-ink hover:text-brand">{row.title}</a>
                  <span className="block text-ink-soft">
                    {row.source ? `${row.source} · ` : ""}{ago(row.heard_at)} · {row.minutes} min
                    {row.times > 1 ? ` · heard ${row.times} times` : ""}
                  </span>
                </span>
                <button
                  data-testid={`listened-again-${idOf(row)}`}
                  onClick={() => replay(row)}
                  title="Hear it again — MiLu already has the words"
                  className={`inline-flex min-h-[26px] shrink-0 items-center gap-1 rounded-full border px-2.5 text-[11px] transition-colors ${
                    reading || busy
                      ? "border-brand/60 text-brand"
                      : "border-white/15 text-ink-soft hover:border-brand/50 hover:text-brand"}`}
                >
                  {busy ? <Loader2 className="h-3 w-3 animate-spin" />
                    : reading ? <Square className="h-3 w-3" />
                    : <Radio className="h-3 w-3" />}
                  {busy ? "Getting it…" : reading ? "Stop" : "Again"}
                </button>
                {onQueue && (
                  <button
                    data-testid={`listened-queue-${idOf(row)}`}
                    onClick={() => onQueue(row)}
                    title="Line it up to listen later"
                    className={`inline-flex min-h-[26px] shrink-0 items-center gap-1 rounded-full border px-2.5 text-[11px] transition-colors ${
                      isQueued?.(row)
                        ? "border-brand/60 text-brand"
                        : "border-white/15 text-ink-soft hover:border-brand/50 hover:text-brand"}`}
                  >
                    <ListPlus className="h-3 w-3" /> {isQueued?.(row) ? "Queued" : "Queue"}
                  </button>
                )}
              </li>
            );
          })}
          {rows.length > 6 && (
            <li>
              <button
                data-testid="listen-history-more"
                onClick={() => setShowAll((s) => !s)}
                className="text-[11px] text-ink-soft underline decoration-dotted hover:text-brand"
              >
                {showAll ? "Show fewer" : `Show all ${rows.length}`}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};
